import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { ParticipationConsent, TermsDialog } from "@/components/LegalContent";
import { apiError } from "@/lib/apiError";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  Eraser, ChevronLeft, ChevronRight, ExternalLink, Download,
  CheckCircle2, ShieldCheck, FileSignature, Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API });

const BRAND = {
  primary: "#FF6B35",
  primaryDark: "#E15829",
  ink: "#0F172A",
  muted: "#475569",
  bg: "#FFFFFF",
  soft: "#FFF6F0",
  border: "#FFE0CC",
};

function SignaturePad({ onChange, value }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(true);
  const savedSignature = useRef(value);

  const setup = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * ratio;
    c.height = rect.height * ratio;
    const ctx = c.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = BRAND.ink;
  };

  useEffect(() => {
    const r = () => {
      setup();
      if (savedSignature.current) {
        const image = new Image();
        image.onload = () => {
          const c = canvasRef.current;
          if (!c) return;
          const rect = c.getBoundingClientRect();
          c.getContext("2d").drawImage(image, 0, 0, rect.width, rect.height);
          setEmpty(false);
        };
        image.src = savedSignature.current;
      }
    };
    r();
    window.addEventListener("resize", r);
    return () => window.removeEventListener("resize", r);
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    setEmpty(false);
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    savedSignature.current = canvasRef.current.toDataURL("image/png");
    onChange?.(savedSignature.current);
  };
  const clear = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, c.width, c.height);
    const ratio = window.devicePixelRatio || 1;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = BRAND.ink;
    setEmpty(true);
    savedSignature.current = "";
    onChange?.("");
  };

  return (
    <div className="space-y-2">
      <div className="relative rounded-xl border-2 border-dashed bg-white" style={{ borderColor: BRAND.border, touchAction: "none" }}>
        <canvas
          ref={canvasRef}
          data-testid="signature-canvas"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          className="w-full h-44 cursor-crosshair rounded-xl"
        />
        {empty && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm" style={{ color: BRAND.muted }}>
            Sign here using your finger or mouse
          </div>
        )}
      </div>
      <div className="flex justify-end">
        <button type="button" onClick={clear} data-testid="signature-clear" className="text-xs inline-flex items-center gap-1 hover:opacity-80" style={{ color: BRAND.primaryDark }}>
          <Eraser className="w-3.5 h-3.5" /> Clear signature
        </button>
      </div>
    </div>
  );
}

function Stepper({ step, total }) {
  return (
    <div className="flex items-center gap-2 mb-6" data-testid="step-indicator">
      {Array.from({ length: total }, (_, i) => (
        <div key={i} className="h-1.5 rounded-full flex-1 transition-all"
          style={{ backgroundColor: i <= step ? BRAND.primary : "#E2E8F0", opacity: i <= step ? 1 : 0.4 }} />
      ))}
    </div>
  );
}

export default function MembershipWidget() {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [info, setInfo] = useState(null);
  const [result, setResult] = useState(null);
  const [showTerms, setShowTerms] = useState(false);

  const [form, setForm] = useState({
    given_names: "", surname: "", date_of_birth: "",
    email: "", phone: "", mobile: "",
    address: "", locality: "", postcode: "",
    dec_membership: false, dec_waec_contact: false, dec_data_storage: false,
    signature: "",
  });

  useEffect(() => {
    api.get("/public/membership/onp-wa/info")
      .then(r => setInfo(r.data))
      .catch(() => setInfo({ party_name: "One Nation Western Australia" }));
  }, []);

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const validateStep1 = () => {
    const required = ["given_names", "surname", "date_of_birth", "email", "address", "locality", "postcode"];
    for (const k of required) {
      if (!form[k]?.trim()) { toast.error(`Please fill in: ${k.replace(/_/g, " ")}`); return false; }
    }
    if (!/\S+@\S+\.\S+/.test(form.email)) { toast.error("Please enter a valid email address"); return false; }
    if (!/^\d{4}$/.test(form.postcode)) { toast.error("Postcode must be 4 digits"); return false; }
    if (!form.phone?.trim() && !form.mobile?.trim()) { toast.error("Please provide a phone number or a mobile number"); return false; }
    if (!form.dec_data_storage) { toast.error("Please agree with the Terms and Conditions before continuing"); return false; }
    return true;
  };

  const submit = async () => {
    if (!form.dec_membership) return toast.error("Please tick the membership confirmation");
    if (!form.dec_waec_contact) return toast.error("Please tick the WAEC contact consent");
    if (!form.signature) return toast.error("Please draw your signature");
    setSubmitting(true);
    try {
      const r = await api.post("/public/membership/onp-wa/submit", form);
      setResult(r.data);
      setStep(3);
      toast.success("Declaration submitted");
    } catch (e) {
      toast.error(apiError(e, "Submission failed. Please try again."));
    } finally { setSubmitting(false); }
  };

  // Native links preserve the user gesture and use the same short-lived capability.
  const pdfFilename = result?.pdf_filename || (result?.id ? `OnP-WA-Declaration-${result.id.slice(0, 8)}.pdf` : "OnP-WA-Declaration.pdf");
  const pdfFallbackUrl = result?.id ? `${API}/public/membership/onp-wa/pdf/${result.id}?token=${encodeURIComponent(result.download_token || "")}` : undefined;

  const Header = () => (
    <div className="text-center mb-5">
      <img src="/static/onp-wa-logo.png" alt="Pauline Hanson's One Nation Western Australia" data-testid="onp-logo"
        className="mx-auto h-24 sm:h-28 mb-4 object-contain transition-transform duration-300 hover:scale-105" />
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-3 transition-colors hover:bg-white" style={{ backgroundColor: BRAND.soft, color: BRAND.primaryDark }}>
        <ShieldCheck className="w-3.5 h-3.5" /> Official WAEC Form FD25
      </div>
      <h1 className="text-2xl font-bold tracking-tight" style={{ color: BRAND.ink }}>Declaration of Party Membership</h1>
      <p className="text-sm mt-1" style={{ color: BRAND.muted }}>{info?.party_name || "One Nation Western Australia"}</p>
    </div>
  );

  return (
    <div className="min-h-screen w-full flex items-start justify-center px-4 py-8" style={{ background: `linear-gradient(180deg, ${BRAND.soft} 0%, #FFFFFF 60%)` }}>
      <TermsDialog open={showTerms} onOpenChange={setShowTerms} variant="membership" organisation={info?.party_name || "One Nation Western Australia"} />
      <div className="w-full max-w-2xl">
        <div className="rounded-2xl shadow-xl border bg-white overflow-hidden transition-shadow duration-300 hover:shadow-2xl" style={{ borderColor: BRAND.border }} data-testid="membership-widget">
          <div className="px-6 sm:px-8 py-7">
            <Header />
            {step < 3 && <Stepper step={step} total={3} />}

            {step === 0 && (
              <div className="space-y-5" data-testid="step-intro">
                <p className="text-base leading-relaxed" style={{ color: BRAND.muted }}>
                  Thanks for supporting <strong style={{ color: BRAND.ink }}>One Nation Western Australia</strong>. This form
                  helps the party stay registered with the Western Australian Electoral Commission under the
                  <em> Electoral Act 1907</em> — and it only takes a couple of minutes.
                </p>
                <div className="rounded-xl p-4 border transition-all duration-200 hover:shadow-md" style={{ backgroundColor: BRAND.soft, borderColor: BRAND.border }}>
                  <div className="text-sm font-semibold mb-1" style={{ color: BRAND.ink }}>How it works</div>
                  <ol className="space-y-1.5 text-sm list-decimal list-inside" style={{ color: BRAND.muted }}>
                    <li>You enter your details and tick the required declarations.</li>
                    <li>We use your information to fill out the official <strong>WAEC FD25 Declaration of Party Membership</strong> and apply your signature.</li>
                    <li>You'll be able to download the completed, signed PDF straight away — your copy of the official form.</li>
                  </ol>
                </div>
                <ul className="space-y-2.5 text-base" style={{ color: BRAND.muted }}>
                  <li className="flex items-start gap-2 transition-transform duration-200 hover:translate-x-1"><CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: BRAND.primary }} /> Takes about 2 minutes</li>
                  <li className="flex items-start gap-2 transition-transform duration-200 hover:translate-x-1"><CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: BRAND.primary }} /> For membership administration and party registration verification</li>
                  <li className="flex items-start gap-2 transition-transform duration-200 hover:translate-x-1"><CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: BRAND.primary }} /> You will be able to download your signed PDF</li>
                </ul>
                <Button data-testid="start-btn" onClick={() => setStep(1)}
                  className="w-full font-semibold text-base py-6 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:brightness-110 active:translate-y-0"
                  style={{ backgroundColor: BRAND.primary, color: "#FFFFFF" }}>
                  Get started <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
                <div className="rounded-lg p-3 border flex items-start gap-3 mt-3 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5" style={{ backgroundColor: BRAND.soft, borderColor: BRAND.border }}>
                  <ExternalLink className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: BRAND.primary }} />
                  <div className="text-sm" style={{ color: BRAND.ink }}>
                    <strong>Are you sure your details are correct?</strong> The WA Electoral Commission will not accept this form unless your details exactly match the electoral roll. You can verify your enrolment{" "}
                    <a href="https://www.elections.wa.gov.au/oes#/oec" target="_blank" rel="noreferrer noopener" data-testid="check-registration" className="font-semibold hover:underline" style={{ color: BRAND.primaryDark }}>
                      with the WAEC here
                    </a>{" "}before you continue.
                  </div>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-4" data-testid="step-details">
                <h2 className="font-semibold text-base" style={{ color: BRAND.ink }}>Your details</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label htmlFor="given_names" className="text-xs">Given names *</Label><Input id="given_names" data-testid="f-given-names" value={form.given_names} onChange={e => update("given_names", e.target.value)} /></div>
                  <div><Label htmlFor="surname" className="text-xs">Surname *</Label><Input id="surname" data-testid="f-surname" value={form.surname} onChange={e => update("surname", e.target.value)} /></div>
                  <div><Label htmlFor="dob" className="text-xs">Date of Birth (DD/MM/YYYY) *</Label><Input id="dob" data-testid="f-dob" placeholder="01/01/1980" value={form.date_of_birth} onChange={e => update("date_of_birth", e.target.value)} /></div>
                  <div><Label htmlFor="email" className="text-xs">Email *</Label><Input id="email" type="email" data-testid="f-email" value={form.email} onChange={e => update("email", e.target.value)} /></div>
                  <div className="sm:col-span-2"><Label htmlFor="address" className="text-xs">Street address *</Label><Input id="address" data-testid="f-address" value={form.address} onChange={e => update("address", e.target.value)} /></div>
                  <div><Label htmlFor="locality" className="text-xs">Suburb / town *</Label><Input id="locality" data-testid="f-locality" value={form.locality} onChange={e => update("locality", e.target.value)} /></div>
                  <div><Label htmlFor="postcode" className="text-xs">Postcode *</Label><Input id="postcode" data-testid="f-postcode" maxLength={4} value={form.postcode} onChange={e => update("postcode", e.target.value.replace(/\D/g, ""))} /></div>
                  <div><Label htmlFor="phone" className="text-xs">Phone</Label><Input id="phone" data-testid="f-phone" value={form.phone} onChange={e => update("phone", e.target.value)} /></div>
                  <div><Label htmlFor="mobile" className="text-xs">Mobile</Label><Input id="mobile" data-testid="f-mobile" value={form.mobile} onChange={e => update("mobile", e.target.value)} /></div>
                  <div className="sm:col-span-2 -mt-1"><p className="text-xs italic" style={{ color: BRAND.muted }}>Please provide a phone or mobile number (at least one is required).</p></div>
                </div>
                <p className="text-xs" style={{ color: BRAND.muted }}>State on the form is pre-filled as <strong>WA</strong>. Today's date will appear automatically when the PDF is generated.</p>
                <ParticipationConsent prefix="membership" checkboxTestId="dec-data-storage" checked={form.dec_data_storage} onChange={value => update("dec_data_storage", value)} onTerms={() => setShowTerms(true)} />
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" onClick={() => setStep(0)} data-testid="back-1"><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
                  <Button data-testid="next-1" onClick={() => { if (validateStep1()) setStep(2); }} className="flex-1 font-semibold transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:brightness-110 active:translate-y-0" style={{ backgroundColor: BRAND.primary, color: "#FFFFFF" }}>
                    Continue <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5" data-testid="step-sign">
                <div>
                  <h2 className="font-semibold text-base" style={{ color: BRAND.ink }}>Declaration & signature</h2>
                  <p className="text-xs mt-1" style={{ color: BRAND.muted }}>The two tick boxes below will appear on your signed PDF as required by the WA Electoral Commission's FD25 form.</p>
                </div>
                <div className="space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border transition-all duration-200 hover:bg-orange-50/60 hover:border-orange-300 hover:shadow-sm" style={{ borderColor: form.dec_membership ? BRAND.primary : "#E2E8F0" }}>
                    <input type="checkbox" data-testid="dec-membership" checked={form.dec_membership} onChange={e => update("dec_membership", e.target.checked)} className="mt-0.5 w-5 h-5 flex-shrink-0" style={{ accentColor: BRAND.primary }} />
                    <span className="text-sm" style={{ color: BRAND.ink }}>
                      I confirm that I am a member of <strong>One Nation Western Australia</strong> and I consent to the party relying on my membership for the purpose of qualifying as an eligible political party for registration under the <em>Electoral Act 1907</em>. <span style={{ color: BRAND.primary }}>*</span>
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border transition-all duration-200 hover:bg-orange-50/60 hover:border-orange-300 hover:shadow-sm" style={{ borderColor: form.dec_waec_contact ? BRAND.primary : "#E2E8F0" }}>
                    <input type="checkbox" data-testid="dec-waec-contact" checked={form.dec_waec_contact} onChange={e => update("dec_waec_contact", e.target.checked)} className="mt-0.5 w-5 h-5 flex-shrink-0" style={{ accentColor: BRAND.primary }} />
                    <span className="text-sm" style={{ color: BRAND.ink }}>
                      I give permission for the <strong>Western Australian Electoral Commission</strong> to contact me to verify the requirements for the above party's eligibility for registration. <span style={{ color: BRAND.primary }}>*</span>
                    </span>
                  </label>
                </div>
                <div>
                  <Label className="text-xs">Your signature *</Label>
                  <SignaturePad value={form.signature} onChange={(v) => update("signature", v)} />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" onClick={() => setStep(1)} data-testid="back-2" disabled={submitting}><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
                  <Button data-testid="submit-btn" onClick={submit} disabled={submitting}
                    className="flex-1 font-semibold transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:brightness-110 active:translate-y-0 disabled:hover:translate-y-0 disabled:hover:shadow-none"
                    style={{ backgroundColor: BRAND.primary, color: "#FFFFFF" }}>
                    {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating PDF...</>) : (<><FileSignature className="w-4 h-4 mr-2" /> Submit declaration</>)}
                  </Button>
                </div>
              </div>
            )}

            {step === 3 && result && (
              <div className="text-center space-y-5 py-4" data-testid="step-thanks">
                <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: BRAND.soft }}>
                  <CheckCircle2 className="w-9 h-9" style={{ color: BRAND.primary }} />
                </div>
                <div>
                  <h2 className="text-xl font-bold" style={{ color: BRAND.ink }}>Declaration submitted</h2>
                  <p className="text-sm mt-1" style={{ color: BRAND.muted }}>A signed copy of your FD25 declaration is ready to download.</p>
                </div>
                <a
                  data-testid="download-btn"
                  href={pdfFallbackUrl}
                  download={pdfFilename}
                  className="inline-flex items-center justify-center w-full font-semibold py-3 rounded-md transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:brightness-110 active:translate-y-0"
                  style={{ backgroundColor: BRAND.primary, color: "#FFFFFF" }}
                >
                  <Download className="w-5 h-5 mr-2" /> Download my signed PDF
                </a>
                {result?.id && (
                  <a
                    data-testid="view-pdf-btn"
                    href={`${pdfFallbackUrl}&inline=1`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block text-sm font-medium hover:opacity-80 underline-offset-4 hover:underline"
                    style={{ color: BRAND.primaryDark }}
                  >
                    Or open the PDF in a new tab
                  </a>
                )}
                <p data-testid="membership-download-notice" className="text-xs" style={{ color: BRAND.muted }}>Save your copy now. The online download link expires after one hour. For another copy, contact One Nation WA.</p>
              </div>
            )}
          </div>
          <div className="px-6 sm:px-8 py-3 border-t text-center text-xs" style={{ backgroundColor: BRAND.soft, borderColor: BRAND.border, color: BRAND.muted }}>
            Form FD25 v1.2 — Western Australian Electoral Commission
          </div>
        </div>
        <div className="text-center text-xs mt-4 space-y-1" style={{ color: BRAND.muted }}>
          <div>
            <a href="https://wa.onenation.org.au" target="_blank" rel="noreferrer noopener" data-testid="onp-website-link" className="font-semibold hover:underline" style={{ color: BRAND.primaryDark }}>
              Visit One Nation Western Australia →
            </a>
          </div>
          <div>Powered by Civipen</div>
          <div><button type="button" data-testid="membership-terms-link" onClick={() => setShowTerms(true)} className="underline underline-offset-2 hover:opacity-75 transition-opacity">Terms and Conditions</button></div>
        </div>
      </div>
    </div>
  );
}
